import { NoPhotography, OpenInNew } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import {
  MarketplaceProductStatus,
  StoreProductStatus,
  AmazonListingItemStatus,
} from "./ProductList";
import { OutOfCriteriaReasonsDialog } from "./OutOfCriteriaReasonsDialog";
import { MinMaxPrice } from "./render-cells/MinMaxPrice";
import { Link } from "react-router-dom";
import { CompetitionDisplay } from "./render-cells/CompetitionDisplay";
import { Copyable } from "../../../../components/copyable/Copyable";

export type TabColumnInfo = {
  path: string;
  storeProductStatus?: StoreProductStatus;
  marketplaceProductStatus?: MarketplaceProductStatus;
  amazonListingItemStatus?: AmazonListingItemStatus;
  hasRowSelection?: boolean;
  hasDescription?: boolean;
  columns?: string[];
};

const standartColumns = [
  "productImageUrl",
  "marketplaceProduct.itemName",
  "asin",
  "price",
  "competition",
  "supplier.cost",
  "supplier.deliveryDays",
  "supplier.stock",
  "create",
];

const amazonColumns = [...standartColumns];

export const tabColumnMapping: TabColumnInfo[] = [
  {
    amazonListingItemStatus: "DISCOVERABLE",
    path: "discoverable",
    columns: standartColumns,
  },
  {
    amazonListingItemStatus: "BUYABLE",
    path: "buyable",
    columns: standartColumns,
  },
  {
    storeProductStatus: "NO_DATA",
    path: "notFound",
    columns: ["asin", "create"],
  },
  /*
  {
    storeProductStatus: "DELETED",
    path: "deleted",
    columns: ["asin", "create"],
  },
  */
  {
    marketplaceProductStatus: "ACTIVE",
    storeProductStatus: "OUT_OF_CRITERIA",
    path: "outOfCriteria",
  },
  {
    marketplaceProductStatus: "ACTIVE",
    storeProductStatus: "PENDING_APPROVE",
    path: "pendingApproval",
    hasRowSelection: true,
    columns: standartColumns,
  },
  {
    marketplaceProductStatus: "ACTIVE",
    storeProductStatus: "ACTIVE",
    path: "active",
    columns: amazonColumns,
  },
  {
    marketplaceProductStatus: "ACTIVE",
    storeProductStatus: "INACTIVE",
    path: "inactive",
    columns: amazonColumns,
  },
];

export const getStandartInventoryColumns = (
  translate: (_key: string) => string,
  locales: Intl.LocalesArgument,
): GridColDef<GridValidRowModel>[] => [
  {
    field: "productImageUrl",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.image",
    ),
    sortable: false,
    renderCell: (params) => (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        {params.value ? (
          <img src={params.value} alt="Product Thumbnail" width="64px" />
        ) : (
          <NoPhotography />
        )}
      </Box>
    ),
  },
  {
    field: "marketplaceProduct.itemName",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.itemName",
    ),
    valueGetter: (_, row) => row.itemName,
    valueFormatter: (value: string) =>
      value ??
      translate(
        "pages.inventoryManagement.inventory.list.fields.emptyItemName",
      ),
    flex: 1,
    renderCell: (params) => {
      return params.value ? (
        <Typography
          component={Link}
          to={`/inventory/product/${params.row.asin.toUpperCase()}`}
          variant="body2"
          color="primary"
          noWrap
          sx={{ cursor: "pointer", textDecoration: "none" }}
        >
          {params.value}
        </Typography>
      ) : (
        ""
      );
    },
  },
  {
    field: "asin",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.asin",
    ),
    minWidth: 150,
    flex: 1,
    valueFormatter: (value: string) => value.toUpperCase(),
    renderCell: (params) => {
      return params.value ? (
        <>
          <Copyable initialLabel={params.value.toUpperCase()} />
          <IconButton
            component={Link}
            to={`https://www.amazon.com/dp/${params.value}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{
              color: "primary.main",
              "&:hover": { color: "primary.dark" },
            }}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </>
      ) : (
        ""
      );
    },
  },
  {
    field: "price",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.price",
    ),
    minWidth: 180,
    renderCell: (params) => {
      const minPrice = params.row.minPrice;
      const price = params.row.price;
      const pricePercentage = params.row.profitPercentage;
      const maxPrice = params.row.maxPrice;

      return (
        <MinMaxPrice
          minPrice={minPrice}
          price={price}
          maxPrice={maxPrice}
          pricePercentage={pricePercentage}
        />
      );
    },
  },
  {
    field: "competition",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.competition",
    ),
    minWidth: 240,
    sortable: false,
    renderCell: (params) => {
      const productDetail = params.row;

      return (
        <CompetitionDisplay
          buyboxPrice={productDetail?.buyBoxPriceAmount}
          buyboxIsWon={productDetail?.buyBoxWinner}
          buyboxPercentage={
            productDetail
              ? productDetail?.competitiveBuyBoxMarginPercentage
              : undefined
          }
          lowestPrice={productDetail?.lowestPriceAmount}
          lowestIsWon={productDetail?.myOfferLowest}
          lowestPercentage={
            productDetail
              ? productDetail?.competitiveMinMarginPercentage
              : undefined
          }
          totalOfferCount={productDetail?.totalOfferCount}
        />
      );
    },
  },
  {
    field: "supplier.stock",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.stock",
    ),
    valueGetter: (_, row) => row.stock,
    renderCell: (params) => {
      return (
        params.value ??
        translate("pages.inventoryManagement.inventory.list.fields.outOfStock")
      );
    },
  },
  {
    field: "supplier.deliveryDays",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.deliveryDays",
    ),
    valueGetter: (_, row) => row.supplierDays,
    renderCell: (params) => {
      return params.value
        ? params.value +
            " " +
            translate("pages.stores.settings.generalSettings.days")
        : "";
    },
  },
  {
    field: "outOfCriteriaReasons",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.outOfCriteriaReasons",
    ),
    flex: 1,
    renderCell: (params) => {
      if (
        !params.value ||
        !Array.isArray(params.value) ||
        params.value.length === 0
      ) {
        return null;
      }

      return <OutOfCriteriaReasonsDialog reasons={params.value} />;
    },
  },

  {
    field: "create",
    headerName: translate(
      "pages.inventoryManagement.inventory.list.fields.created",
    ),
    flex: 1,
    sortable: false,
    valueFormatter: (value: string) => {
      const date = new Date(value).toLocaleDateString(locales);
      const time = new Date(value).toLocaleTimeString(locales);

      return `${date} ${time}`;
    },
  },
];
